import React, { useState } from "react";
import { enemies } from "../data/battleData";

const TIER_STYLE = {
  easy:   { bg: "bg-green-100",  text: "text-green-700",  label: "Easy" },
  medium: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Medium" },
  hard:   { bg: "bg-red-100",    text: "text-red-700",    label: "Hard" },
};

// Maps the Tailwind color base name from enemy.color → CSS hex pair for gradients
const COLOR_MAP = {
  blue:    ["#3b82f6", "#1d4ed8"],
  orange:  ["#f97316", "#ea580c"],
  red:     ["#ef4444", "#991b1b"],
  purple:  ["#a855f7", "#6d28d9"],
  yellow:  ["#eab308", "#b45309"],
  pink:    ["#ec4899", "#9d174d"],
  teal:    ["#14b8a6", "#0f766e"],
  green:   ["#22c55e", "#15803d"],
  lime:    ["#84cc16", "#4d7c0f"],
  gray:    ["#6b7280", "#374151"],
  indigo:  ["#6366f1", "#3730a3"],
  amber:   ["#f59e0b", "#92400e"],
  cyan:    ["#06b6d4", "#0369a1"],
  violet:  ["#8b5cf6", "#5b21b6"],
  rose:    ["#f43f5e", "#9f1239"],
  emerald: ["#10b981", "#065f46"],
  slate:   ["#64748b", "#1e293b"],
  stone:   ["#78716c", "#292524"],
  zinc:    ["#71717a", "#27272a"],
};

function getHeaderGrad(enemy) {
  const match = enemy.color.match(/from-(\w+)-/);
  const base = match ? match[1] : "gray";
  const [c1, c2] = COLOR_MAP[base] || COLOR_MAP.gray;
  return `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`;
}

const PathogenModal = ({ enemy, onClose }) => {
  const correctTreatments = enemy.treatments.filter(t => t.type === "correct");
  const headerGrad = getHeaderGrad(enemy);
  const tier = TIER_STYLE[enemy.tier] || TIER_STYLE.easy;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.75)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl flex flex-col"
        style={{ maxHeight: "88vh" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="relative flex-shrink-0 overflow-hidden flex flex-col justify-end"
          style={{ minHeight: enemy.image ? 240 : "auto" }}
        >

          {/* Background: full image or colour gradient */}
          {enemy.image ? (
            <img
              src={enemy.image}
              alt={enemy.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : null}

          {/* Gradient overlay — always present so text is readable */}
          <div
            className="absolute inset-0"
            style={{
              background: enemy.image
                ? "linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.88) 100%)"
                : headerGrad,
            }}
          />

          {/* Close button pinned to top-right */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white text-xl leading-none z-10"
          >✕</button>

          {/* Text content pinned to bottom */}
          <div className="relative px-5 pb-5 pt-16">
            {!enemy.image && (
              <div className="text-6xl text-center mb-3 drop-shadow">{enemy.emoji}</div>
            )}

            <h2 className="text-white font-extrabold text-xl text-center leading-tight drop-shadow">{enemy.name}</h2>
            <p className="text-white/80 text-xs text-center mt-1 italic drop-shadow">{enemy.disease}</p>

            <div className="flex justify-center gap-2 mt-3 flex-wrap">
              <span className="text-xs px-2.5 py-1 rounded-full bg-white/20 text-white font-medium backdrop-blur-sm">
                {enemy.type}
              </span>
              <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${tier.bg} ${tier.text}`}>
                {tier.label}
              </span>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="bg-white overflow-y-auto flex-1" style={{ overscrollBehavior: "contain" }}>

          {/* Appearance */}
          <div className="px-5 py-4 border-b border-gray-100">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">🔬 Appearance</p>
            <p className="text-sm text-gray-700 leading-relaxed">{enemy.description}</p>
          </div>

          {/* Diagnosis & Culture */}
          {enemy.diagnosis && (
            <div className="px-5 py-4 border-b border-gray-100">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">🧫 Diagnosis &amp; Culture</p>
              <p className="text-sm text-gray-700 leading-relaxed">{enemy.diagnosis}</p>
            </div>
          )}

          {/* First-line treatments */}
          {correctTreatments.length > 0 && (
            <div className="px-5 py-4 border-b border-gray-100">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">⚔️ First-Line Treatment</p>
              <div className="flex flex-col gap-3">
                {correctTreatments.map(t => (
                  <div key={t.id} className="flex items-start gap-2.5">
                    <span className="text-base flex-shrink-0 mt-0.5">{t.icon}</span>
                    <div>
                      <span className="text-sm font-semibold text-gray-800">{t.name}</span>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{t.explanation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fun Fact */}
          <div className="px-5 py-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">💡 Fun Fact</p>
            <p className="text-sm text-gray-600 leading-relaxed italic">{enemy.funFact}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Pokedex = ({ defeatedEnemies = [], onClose }) => {
  const [selected, setSelected] = useState(null);
  const defeated = new Set(defeatedEnemies);
  const count = enemies.filter(e => defeated.has(e.id)).length;
  const total = enemies.length;

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <button onClick={onClose} className="text-sm text-gray-300 hover:text-white">← Back</button>
        <span className="font-bold text-white text-sm">🧫 Pathogens</span>
        <span className="text-xs text-gray-400">{count}/{total} found</span>
      </div>

      {/* Progress bar */}
      <div className="px-4 pt-3 pb-3 bg-gray-900 flex-shrink-0">
        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-teal-400 to-emerald-400 rounded-full transition-all duration-500"
            style={{ width: `${total > 0 ? (count / total) * 100 : 0}%` }}
          />
        </div>
        <p className="text-xs text-gray-400 mt-1.5 text-center">
          {count === 0 && "Defeat pathogens in battle to discover them"}
          {count > 0 && count < total && `${count} of ${total} pathogens discovered`}
          {count === total && "🎉 Complete collection!"}
        </p>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {enemies.map(enemy => {
            const known = defeated.has(enemy.id);
            const tier = TIER_STYLE[enemy.tier] || TIER_STYLE.easy;
            return (
              <button
                key={enemy.id}
                onClick={() => known && setSelected(enemy)}
                className={`rounded-2xl border-2 transition-all overflow-hidden relative ${
                  known && enemy.image
                    ? "border-gray-200 hover:border-teal-300 shadow-sm active:scale-95"
                    : known
                    ? "bg-white border-gray-200 hover:border-teal-300 shadow-sm active:scale-95 flex flex-col items-center gap-1.5 p-3"
                    : "bg-gray-800 border-gray-700 cursor-default flex flex-col items-center gap-1.5 p-3"
                }`}
                style={{ minHeight: known && enemy.image ? 100 : "auto" }}
              >
                {known && enemy.image ? (
                  <>
                    {/* Full-cover image */}
                    <img
                      src={enemy.image}
                      alt={enemy.nickname}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    {/* Dark gradient at bottom for text */}
                    <div
                      className="absolute inset-0"
                      style={{ background: "linear-gradient(to bottom, transparent 30%, rgba(0,0,0,0.7) 100%)" }}
                    />
                    {/* Text pinned to bottom */}
                    <div className="relative flex flex-col items-center gap-1 mt-auto px-1 pb-2 pt-10">
                      <div className="text-xs font-semibold text-center leading-tight text-white drop-shadow">
                        {enemy.nickname}
                      </div>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${tier.bg} ${tier.text}`}>
                        {tier.label}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={`text-3xl ${!known && "grayscale opacity-30"}`}>
                      {known ? enemy.emoji : "🔬"}
                    </div>
                    <div className={`text-xs font-semibold text-center leading-tight ${known ? "text-gray-800" : "text-gray-600"}`}>
                      {known ? enemy.nickname : "???"}
                    </div>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${tier.bg} ${tier.text}`}>
                      {tier.label}
                    </span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Popup card */}
      {selected && (
        <PathogenModal enemy={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};

export default Pokedex;
